// =====================================================================
// PrintingCatalogsAdmin.jsx - Admin catalog Printing (M-003 Faza 2)
// =====================================================================
// Pagina ADMIN: CRUD pentru PrintingClient + PrintingTaskTemplate.
// Acces: doar `printing_jobs.manage_catalog.all` (ADMIN+).
//
// 2 tabs:
//   - Clients (5 entries seedate; add/edit/soft delete)
//   - Tâches (56 entries seedate; sortat by usageCount DESC; add/edit/soft delete)
//
// Pattern: inline edit (click pe rand → modifica direct + save buton),
// minimalist, fara modal separat.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  printingClients as clientsApi,
  printingTaskTemplates as templatesApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';

export default function PrintingCatalogsAdmin() {
  const { can } = useAuth();
  const canManage = can('printing_jobs.manage_catalog.all');

  const [tab, setTab] = useState('clients'); // 'clients' | 'templates'

  if (!canManage) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, margin: 0 }}>Accès interdit</h1>
        <p style={{ color: '#64748b', marginTop: 8 }}>
          Vous n'avez pas la permission de gérer les catalogues d'impression.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 16, fontSize: 13 }}>
      <div style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Catalogues Impression</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
          Gestion des clients et tâches utilisés pour les jobs d'impression.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
        <TabBtn label="Clients" active={tab === 'clients'} onClick={() => setTab('clients')} />
        <TabBtn label="Tâches" active={tab === 'templates'} onClick={() => setTab('templates')} />
      </div>

      {tab === 'clients' && <ClientsTab />}
      {tab === 'templates' && <TemplatesTab />}
    </div>
  );
}

function TabBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick}
            style={{
              padding: '10px 18px', fontSize: 13, fontWeight: active ? 600 : 400,
              background: 'transparent', border: 'none',
              borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
              color: active ? '#1d4ed8' : '#64748b',
              cursor: 'pointer',
              marginBottom: -1,
            }}>
      {label}
    </button>
  );
}

// =====================================================================
// CLIENTS TAB
// =====================================================================
export function ClientsTab() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [editing, setEditing]   = useState(null); // {id, code, label, isActive, notes}
  const [creating, setCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await clientsApi.list(showInactive ? {} : { isActive: true });
      const arr = Array.isArray(res) ? res : (res.items || []);
      setItems(arr.sort((a, b) => (a.label || '').localeCompare(b.label || '', 'fr')));
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur chargement clients');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const handleStartCreate = () => {
    setCreating(true);
    setEditing({ code: '', label: '', isActive: true, notes: '' });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.code?.trim() || !editing.label?.trim()) {
      setError('Code et label sont obligatoires.');
      return;
    }
    setError(null);
    try {
      if (creating) {
        await clientsApi.create({
          code: editing.code.trim(),
          label: editing.label.trim(),
          isActive: !!editing.isActive,
          notes: editing.notes?.trim() || undefined,
        });
      } else {
        await clientsApi.update(editing.id, {
          label: editing.label.trim(),
          isActive: !!editing.isActive,
          notes: editing.notes?.trim() || undefined,
        });
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur sauvegarde');
    }
  };

  const handleSoftDelete = async (item) => {
    if (!confirm(`Désactiver le client ${item.code} — ${item.label} ?\n\n(Suppression douce; les jobs existants restent intacts.)`)) return;
    try {
      await clientsApi.remove(item.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur suppression');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Afficher les inactifs
        </label>
        {!creating && (
          <button onClick={handleStartCreate}
                  style={primaryBtn}>+ Nouveau client</button>
        )}
      </div>

      {error && (
        <div style={errorBoxStyle}>{error}</div>
      )}

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: 'white' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={thStyle}>Code</th>
              <th style={thStyle}>Label</th>
              <th style={thStyle}>Notes</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actif</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Jobs</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {creating && editing && (
              <EditRowClient row={editing} setRow={setEditing} onSave={handleSave}
                             onCancel={() => { setEditing(null); setCreating(false); setError(null); }}
                             isNew />
            )}
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Chargement...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Aucun client.</td></tr>
            ) : items.map(item => (
              editing && !creating && editing.id === item.id ? (
                <EditRowClient key={item.id} row={editing} setRow={setEditing} onSave={handleSave}
                               onCancel={() => { setEditing(null); setError(null); }} />
              ) : (
                <tr key={item.id} style={{ borderBottom: '1px solid #e2e8f0', background: item.isActive ? 'white' : '#fafafa' }}>
                  <td style={{ ...tdStyle, fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 500 }}>{item.code}</td>
                  <td style={tdStyle}>{item.label}</td>
                  <td style={{ ...tdStyle, color: '#64748b', fontSize: 12 }}>{item.notes || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {item.isActive ? <span style={badgeActiveStyle}>Actif</span> : <span style={badgeInactiveStyle}>Inactif</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>
                    {item._count?.jobs ?? '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button onClick={() => setEditing({ ...item })} style={smallBtn}>Modifier</button>
                    {item.isActive && (
                      <button onClick={() => handleSoftDelete(item)} style={{ ...smallBtn, marginLeft: 4, color: '#991b1b', borderColor: '#fca5a5' }}>
                        Désactiver
                      </button>
                    )}
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditRowClient({ row, setRow, onSave, onCancel, isNew }) {
  return (
    <tr style={{ background: '#eff6ff', borderBottom: '1px solid #93c5fd' }}>
      <td style={tdStyle}>
        {isNew ? (
          <input value={row.code} onChange={e => setRow({ ...row, code: e.target.value.toUpperCase() })}
                 placeholder="ROSSEL" style={{ ...inlineInput, fontFamily: 'ui-monospace, Menlo, monospace', textTransform: 'uppercase' }} />
        ) : (
          <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 500 }}>{row.code}</span>
        )}
      </td>
      <td style={tdStyle}>
        <input value={row.label} onChange={e => setRow({ ...row, label: e.target.value })}
               placeholder="Rossel &amp; Cie" style={inlineInput} />
      </td>
      <td style={tdStyle}>
        <input value={row.notes || ''} onChange={e => setRow({ ...row, notes: e.target.value })}
               placeholder="(optionnel)" style={inlineInput} />
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <input type="checkbox" checked={!!row.isActive} onChange={e => setRow({ ...row, isActive: e.target.checked })} />
      </td>
      <td style={tdStyle}></td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <button onClick={onSave} style={{ ...smallBtn, background: '#2563eb', color: 'white', borderColor: '#2563eb' }}>Enregistrer</button>
        <button onClick={onCancel} style={{ ...smallBtn, marginLeft: 4 }}>Annuler</button>
      </td>
    </tr>
  );
}

// =====================================================================
// TEMPLATES TAB
// =====================================================================
export function TemplatesTab() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [editing, setEditing]   = useState(null);
  const [creating, setCreating] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (showInactive) params.includeInactive = true;
      const res = await templatesApi.list(params);
      const arr = Array.isArray(res) ? res : (res.items || []);
      // backend sortat by usageCount DESC; pastram ordinea
      setItems(arr);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur chargement tâches');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const handleStartCreate = () => {
    setCreating(true);
    setEditing({ label: '', isActive: true, usageCount: 0 });
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.label?.trim()) {
      setError('Label est obligatoire.');
      return;
    }
    setError(null);
    try {
      if (creating) {
        await templatesApi.create({
          label: editing.label.trim(),
          isActive: !!editing.isActive,
        });
      } else {
        await templatesApi.update(editing.id, {
          label: editing.label.trim(),
          isActive: !!editing.isActive,
        });
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur sauvegarde');
    }
  };

  const handleSoftDelete = async (item) => {
    if (!confirm(`Désactiver la tâche "${item.label}" ?\n\n(Suppression douce; les jobs existants restent intacts.)`)) return;
    try {
      await templatesApi.remove(item.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur suppression');
    }
  };

  const filtered = search.trim()
    ? items.filter(t => (t.label || '').toLowerCase().includes(search.trim().toLowerCase()))
    : items;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: '1 1 auto' }}>
          <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
                 style={{ ...inlineInput, maxWidth: 240 }} />
          <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Afficher les inactifs
          </label>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{filtered.length} / {items.length}</span>
        </div>
        {!creating && (
          <button onClick={handleStartCreate} style={primaryBtn}>+ Nouvelle tâche</button>
        )}
      </div>

      {error && (
        <div style={errorBoxStyle}>{error}</div>
      )}

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: 'white' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={thStyle}>Label</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Usage</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actif</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {creating && editing && (
              <EditRowTemplate row={editing} setRow={setEditing} onSave={handleSave}
                               onCancel={() => { setEditing(null); setCreating(false); setError(null); }}
                               isNew />
            )}
            {loading ? (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Chargement...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Aucune tâche.</td></tr>
            ) : filtered.map(item => (
              editing && !creating && editing.id === item.id ? (
                <EditRowTemplate key={item.id} row={editing} setRow={setEditing} onSave={handleSave}
                                 onCancel={() => { setEditing(null); setError(null); }} />
              ) : (
                <tr key={item.id} style={{ borderBottom: '1px solid #e2e8f0', background: item.isActive ? 'white' : '#fafafa' }}>
                  <td style={tdStyle}>{item.label}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>
                    {item.usageCount ?? 0}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {item.isActive ? <span style={badgeActiveStyle}>Actif</span> : <span style={badgeInactiveStyle}>Inactif</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button onClick={() => setEditing({ ...item })} style={smallBtn}>Modifier</button>
                    {item.isActive && (
                      <button onClick={() => handleSoftDelete(item)} style={{ ...smallBtn, marginLeft: 4, color: '#991b1b', borderColor: '#fca5a5' }}>
                        Désactiver
                      </button>
                    )}
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditRowTemplate({ row, setRow, onSave, onCancel, isNew }) {
  return (
    <tr style={{ background: '#eff6ff', borderBottom: '1px solid #93c5fd' }}>
      <td style={tdStyle}>
        <input value={row.label} onChange={e => setRow({ ...row, label: e.target.value })}
               placeholder="Coiffes DSO" style={inlineInput} autoFocus={isNew} />
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', color: '#94a3b8' }}>
        {row.usageCount ?? 0}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <input type="checkbox" checked={!!row.isActive} onChange={e => setRow({ ...row, isActive: e.target.checked })} />
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <button onClick={onSave} style={{ ...smallBtn, background: '#2563eb', color: 'white', borderColor: '#2563eb' }}>Enregistrer</button>
        <button onClick={onCancel} style={{ ...smallBtn, marginLeft: 4 }}>Annuler</button>
      </td>
    </tr>
  );
}

// =====================================================================
// SHARED STYLES
// =====================================================================
const thStyle = {
  padding: '10px 12px', textAlign: 'left', fontWeight: 500, fontSize: 12,
  color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3,
};
const tdStyle = { padding: '8px 12px' };
const inlineInput = {
  width: '100%', padding: '5px 8px', border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: 13, boxSizing: 'border-box',
};
const primaryBtn = {
  padding: '8px 14px', background: '#2563eb', color: 'white',
  border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 500, cursor: 'pointer',
};
const smallBtn = {
  padding: '4px 10px', background: 'white', color: '#1e293b',
  border: '1px solid #d1d5db', borderRadius: 3, fontSize: 12, cursor: 'pointer',
};
const errorBoxStyle = {
  padding: 10, background: '#fee2e2', color: '#991b1b',
  border: '1px solid #fca5a5', borderRadius: 4, fontSize: 13, marginBottom: 10,
};
const badgeActiveStyle = {
  display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
  background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 3,
};
const badgeInactiveStyle = {
  display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
  background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 3,
};
